import { ActionIcon, Box, Button, Drawer, LoadingOverlay, Text, modals, useForm, LoadError, NoAccess } from "@dehub/theme";
import { CheckCircleIcon, WarningIcon, XIcon } from "@phosphor-icons/react";
import { yupResolver } from "mantine-form-yup-resolver";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { BranchApi } from "@/apis";
import bz from "@/features/business/business.module.css";
import { FormSkeleton } from "@/features/business/components/FormSkeleton";
import type { IGeneral } from "@/models/General";
import { ReasonTooltip } from "@/shared/a11y/reason-tooltip";
import { MissingPermission } from "@/shared/a11y/trans-code";
import { message } from "@/utils/message";
import { renderBusinessName } from "@/utils/render-business-name";
import { BranchLogo } from "../components/BranchLogo";
import type { BranchPermissions } from "../permissions";
import type { BranchRecord, DrawerMode } from "../types";
import { BranchAddressStep } from "./BranchAddressStep";
import { BranchInfoStep, type BusinessOption } from "./BranchInfoStep";
import { BranchStepIndicator, type BranchStep } from "./BranchStepIndicator";
import {
  DEFAULT_ADDRESS,
  DEFAULT_INFO,
  DEFAULT_MARKETING,
  buildAddressPayload,
  buildAddressSchema,
  buildInfoPayload,
  buildInfoSchema,
  hasStoredAddress,
  isMarketingEqual,
  toAddressValues,
  toInfoValues,
  toMarketing,
  type BranchAddressValues,
  type BranchInfoValues,
  type BranchMarketing,
} from "./branch-form-model";

type Props = Readonly<{
  opened: boolean;
  mode: DrawerMode;
  record?: BranchRecord;
  perms: BranchPermissions;
  onClose: () => void;
  /** The list should reload: something was saved. */
  onChanged: () => void;
  /** Reports whether the drawer holds unsaved edits (the page guards navigation with it). */
  onDirtyChange?: (dirty: boolean) => void;
}>;

type Zip = { code: string; name: string };

export function BranchDrawer({ opened, mode, record, perms, onClose, onChanged, onDirtyChange }: Props) {
  const { t } = useTranslation("sector");
  const { t: tCommon } = useTranslation("common");
  const general = useSelector((state: { general: IGeneral }) => state.general);
  const branchTypes = general.branchTypes ?? [];
  const zipCodes = (general.zipCodes ?? []) as Zip[];

  const infoSchema = useMemo(() => buildInfoSchema(tCommon as any, t as any), [tCommon, t]);
  const addressSchema = useMemo(() => buildAddressSchema(tCommon as any), [tCommon]);
  const infoForm = useForm<BranchInfoValues>({ initialValues: DEFAULT_INFO, validate: yupResolver(infoSchema) });
  const addressForm = useForm<BranchAddressValues>({ initialValues: DEFAULT_ADDRESS, validate: yupResolver(addressSchema) });

  const [step, setStep] = useState<BranchStep>(1);
  const [branchId, setBranchId] = useState<string | null>(record?.id ?? null);
  const [loading, setLoading] = useState(Boolean(record?.id));
  // The load error text, or null. While set, nothing here may save: the form would be empty.
  const [loadFailed, setLoadFailed] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(Boolean(record?.isConfirmed));
  const [addressSaved, setAddressSaved] = useState(false);
  const [done, setDone] = useState<ReadonlySet<BranchStep>>(() => new Set<BranchStep>());
  const [marketing, setMarketing] = useState<BranchMarketing>(DEFAULT_MARKETING);
  const [marketingBase, setMarketingBase] = useState<BranchMarketing>(DEFAULT_MARKETING);
  const [marketingVersion, setMarketingVersion] = useState(0);
  const [meta, setMeta] = useState({ refCode: record?.refCode ?? null });

  const detailMode = mode === "DETAIL";
  // A confirmed branch stays editable; it only loses the «confirm» step, which it has already passed.
  const readOnly = detailMode;
  const marketingDirty = !isMarketingEqual(marketing, marketingBase);
  const infoDirty = infoForm.isDirty() || marketingDirty;
  const addressDirty = addressForm.isDirty();
  const hasChanges = infoDirty || addressDirty;
  const busy = loading || saving;
  // State updates are async: a ref stops a second click from starting another save in the same tick.
  const savingRef = useRef(false);

  // An existing branch is loaded in full: a list row carries only a few columns.
  const [loadAttempt, setLoadAttempt] = useState(0);
  useEffect(() => {
    if (!record?.id) return;
    let cancelled = false;
    setLoading(true);
    setLoadFailed(null);
    BranchApi.get(record.id)
      .then((data: Record<string, any>) => {
        if (cancelled) return;
        const info = toInfoValues(data);
        infoForm.setInitialValues(info);
        infoForm.setValues(info);
        infoForm.resetDirty(info);
        const address = toAddressValues(data);
        addressForm.setInitialValues(address);
        addressForm.setValues(address);
        addressForm.resetDirty(address);
        const loaded = toMarketing(data);
        setMarketing(loaded);
        setMarketingBase(loaded);
        setMarketingVersion((v) => v + 1);
        setConfirmed(Boolean(data?.isConfirmed ?? record.isConfirmed));
        setAddressSaved(hasStoredAddress(data));
        setMeta({ refCode: data?.refCode ?? record.refCode ?? null });
        setDone(new Set<BranchStep>(hasStoredAddress(data) ? [1, 2] : [1]));
      })
      .catch((error: { message?: string }) => {
        if (cancelled) return;
        setLoadFailed(error?.message ?? tCommon("error.generic"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id, loadAttempt]);

  useEffect(() => {
    onDirtyChange?.(opened && hasChanges);
    return () => onDirtyChange?.(false);
  }, [opened, hasChanges, onDirtyChange]);

  const markDone = (n: BranchStep) => setDone((prev) => new Set<BranchStep>(Array.from(prev).concat(n)));

  const businessOptions = (type: "BUYER" | "SUPPLIER", selected: string[]): BusinessOption[] =>
    (general.businesses ?? [])
      .filter((b: any) => b?.type === type && (b?.businessStatus === "ACTIVE" || selected.includes(b?.id)))
      .map((b: any) => ({
        id: b.id as string,
        refCode: (b.refCode ?? "") as string,
        name: renderBusinessName({ profileName: b.profileName, profileNameEng: b.profileNameEng }),
      }));
  const buyerOptions = useMemo(
    () => businessOptions("BUYER", infoForm.values.buyers),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [general.businesses, infoForm.values.buyers],
  );
  const supplierOptions = useMemo(
    () => businessOptions("SUPPLIER", infoForm.values.suppliers),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [general.businesses, infoForm.values.suppliers],
  );

  // Permission codes behind each action. A missing one blocks the button and is named in its tooltip.
  const infoBlockedCode: string | null = readOnly
    ? null
    : !branchId
      ? perms.add.hasAccess
        ? null
        : perms.codes.add
      : infoDirty && !perms.edit.hasAccess
        ? perms.codes.edit
        : null;
  const addressBlockedCode: string | null = perms.addr.hasAccess ? null : perms.codes.addr;
  const confirmBlockedCode: string | null = perms.edit.hasAccess ? null : perms.codes.edit;
  const infoNeedsSave = !readOnly && (!branchId || infoDirty);
  const addressNeedsSave = !readOnly && addressDirty;

  const focusFirstError = (form: typeof infoForm | typeof addressForm, errors: Record<string, unknown>) => {
    const key = Object.keys(errors)[0];
    if (!key) return;
    const node = (form as any).getInputNode?.(key) as HTMLElement | null | undefined;
    node?.scrollIntoView({ behavior: "smooth", block: "center" });
    node?.focus?.();
  };

  const validateInfo = (): boolean => {
    const result = infoForm.validate();
    if (!result.hasErrors) return true;
    if (step !== 1) setStep(1);
    setTimeout(() => focusFirstError(infoForm, result.errors), 80);
    return false;
  };

  const validateAddress = (): boolean => {
    const result = addressForm.validate();
    if (!result.hasErrors) return true;
    if (step !== 2) setStep(2);
    setTimeout(() => focusFirstError(addressForm, result.errors), 80);
    return false;
  };

  /** Runs one save with the double-submit guard; any failure keeps the drawer open and the step unchanged. */
  const guarded = async (work: () => Promise<void>): Promise<boolean> => {
    if (savingRef.current) return false;
    savingRef.current = true;
    setSaving(true);
    try {
      await work();
      return true;
    } catch (error: unknown) {
      message.error((error as { message?: string })?.message ?? tCommon("error.generic"));
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const persistInfo = async (confirm: boolean): Promise<boolean> => {
    if (!validateInfo()) return false;
    return guarded(async () => {
      const payload = buildInfoPayload(infoForm.values, marketing, confirm || confirmed);
      let successKey = "form.saveSuccess";
      if (branchId) {
        await BranchApi.update(branchId, payload);
      } else {
        const created = (await BranchApi.create(payload)) as { id: string; refCode?: string | null };
        setBranchId(created.id);
        setMeta((prev) => ({ refCode: created.refCode ?? prev.refCode }));
        successKey = "form.createSuccess";
      }
      message.success(t(confirm ? "form.confirmSuccess" : successKey));
      infoForm.setInitialValues(infoForm.values);
      infoForm.resetDirty(infoForm.values);
      setMarketingBase(marketing);
      if (confirm) setConfirmed(true);
      onChanged();
    });
  };

  const persistAddress = async (): Promise<boolean> => {
    if (!branchId || !validateAddress()) return false;
    return guarded(async () => {
      await BranchApi.updateAddress(branchId, buildAddressPayload(addressForm.values) as any);
      message.success(t("form.addressSaveSuccess"));
      addressForm.setInitialValues(addressForm.values);
      addressForm.resetDirty(addressForm.values);
      setAddressSaved(true);
      onChanged();
    });
  };

  const saveInfoIfNeeded = async (): Promise<boolean> => {
    if (!infoNeedsSave) return true;
    if (infoBlockedCode) return false;
    return persistInfo(false);
  };

  const saveAddressIfNeeded = async (): Promise<boolean> => {
    if (!addressNeedsSave) return true;
    if (addressBlockedCode) return false;
    return persistAddress();
  };

  const handleNext = async () => {
    if (savingRef.current) return;
    if (!readOnly && !validateInfo()) return;
    if (!(await saveInfoIfNeeded())) return;
    markDone(1);
    setStep(2);
  };

  const handleDraftExit = async () => {
    if (savingRef.current) return;
    if (step === 1) {
      if (!(await saveInfoIfNeeded())) return;
    } else {
      if (!(await saveInfoIfNeeded())) return;
      if (!(await saveAddressIfNeeded())) return;
    }
    onClose();
  };

  const requestClose = () => {
    if (!hasChanges) {
      onClose();
      return;
    }
    modals.openConfirmModal({
      title: t("drawer.discardTitle"),
      children: <Text fz={15}>{t("drawer.discardText")}</Text>,
      labels: { confirm: t("drawer.discard"), cancel: tCommon("actions.back") },
      confirmProps: { color: "red", className: bz.btn },
      cancelProps: { className: bz.btn },
      centered: true,
      onConfirm: onClose,
    });
  };

  const zipName = (code: string) => zipCodes.find((z) => z.code === code)?.name ?? "";

  const handleConfirm = () => {
    if (confirmBlockedCode || readOnly || confirmed || !branchId || !addressSaved) return;
    const values = infoForm.values;
    const participation = [values.isBuyer ? t("roles.buyer") : null, values.isSupplier ? t("roles.supplier") : null]
      .filter(Boolean)
      .join(" · ");
    const address = [
      zipName(addressForm.values.province),
      zipName(addressForm.values.district),
      zipName(addressForm.values.khoroo),
      addressForm.values.branchAddress,
    ]
      .filter(Boolean)
      .join(", ");
    const typeName = branchTypes.find((item) => item.code === values.type)?.name ?? values.type;

    modals.openConfirmModal({
      withCloseButton: false,
      centered: true,
      radius: "lg",
      title: (
        <Box component="span" className={`${bz.tokens} ${bz.confirmTitleRow}`}>
          <Box component="span" className={`${bz.confirmIcon} ${bz.confirmIconOn}`}>
            <CheckCircleIcon size={20} weight="bold" aria-hidden="true" />
          </Box>
          <Text component="span" className={bz.confirmTitle}>
            {t("confirm.title")}
          </Text>
        </Box>
      ),
      children: (
        <Box className={`${bz.tokens} ${bz.confirm}`}>
          <Box className={bz.summaryCard}>
            <BranchLogo logo={marketing.logo} name={values.name} nameEng={values.nameEng} />
            <Box className={bz.summaryText}>
              <Text component="span" className={bz.summaryName}>
                {renderBusinessName({ profileName: values.name, profileNameEng: values.nameEng })}
              </Text>
              <Text component="span" className={bz.muted} fz={14}>
                {[typeName, participation].filter(Boolean).join(" · ")}
              </Text>
              <Text component="span" className={bz.muted} fz={14}>
                {address}
              </Text>
            </Box>
            <Text component="span" className={bz.mono} translate="no">
              {meta.refCode ?? "-"}
            </Text>
          </Box>
          <Box className={bz.warnNote} role="note">
            <WarningIcon className={bz.warnIcon} size={18} aria-hidden="true" />
            <Text component="span">{t("confirm.warning")}</Text>
          </Box>
        </Box>
      ),
      labels: { confirm: tCommon("confirm.confirm"), cancel: tCommon("confirm.back") },
      confirmProps: { color: "green", className: bz.btn },
      cancelProps: { className: bz.btn },
      onConfirm: async () => {
        if (!validateInfo() || !validateAddress()) return;
        if (!(await saveAddressIfNeeded())) return;
        // PUT /prt/branch/:id with isConfirmed: also saves any pending information edits.
        if (await persistInfo(true)) onClose();
      },
    });
  };

  const isNew = !branchId;
  const displayName = renderBusinessName({ profileName: infoForm.values.name, profileNameEng: infoForm.values.nameEng }, "");

  // Every locked control says why: a permission code, an unloaded record or a missing earlier step.
  const lockedReason = (n: BranchStep): string | null => {
    if (n === 1) return null;
    if (loadFailed) return t("drawer.loadFailedTitle");
    return branchId ? null : t("steps.locked");
  };

  const missing = (code: string | null) => (code ? <MissingPermission code={code} /> : null);
  const loadReason = loadFailed ? t("drawer.loadFailedTitle") : null;
  const draftReason = loadReason ?? (step === 1 ? missing(infoNeedsSave ? infoBlockedCode : null) : missing(infoNeedsSave ? infoBlockedCode : addressNeedsSave ? addressBlockedCode : null));
  const nextReason = loadReason ?? missing(infoNeedsSave ? infoBlockedCode : null);
  const confirmReason =
    loadReason ??
    missing(confirmBlockedCode) ??
    (!addressSaved ? t("drawer.confirmNeedsAddress") : null);

  const renderStep = () => {
    if (loading) return <FormSkeleton />;
    if (loadFailed) {
      return (
        <LoadError
          title={t("drawer.loadFailedTitle")}
          message={`${loadFailed} ${t("drawer.loadFailedText")}`}
          retryLabel={tCommon("actions.retry")}
          onRetry={() => setLoadAttempt((n) => n + 1)}
        />
      );
    }
    if (step === 1) {
      if (!record && !perms.add.hasAccess) {
        return <NoAccess title={t("drawer.noAccessTitle")} description={<MissingPermission code={perms.codes.add} />} />;
      }
      return (
        <BranchInfoStep
          form={infoForm}
          readOnly={readOnly}
          branchTypes={branchTypes}
          buyerOptions={buyerOptions}
          supplierOptions={supplierOptions}
          marketing={marketing}
          version={marketingVersion}
          branchId={branchId}
          onMarketingChange={setMarketing}
        />
      );
    }
    if (!branchId) return <NoAccess title={t("steps.lockedTitle")} description={t("steps.lockedText")} />;
    return <BranchAddressStep form={addressForm} readOnly={readOnly} />;
  };

  return (
    <Drawer
      opened={opened}
      onClose={requestClose}
      position="right"
      size="min(720px, 100%)"
      padding={0}
      withCloseButton={false}
      classNames={{ content: `${bz.tokens} ${bz.drawerRoot}`, body: bz.drawerBody }}
    >
      <Box className={bz.drawerHead}>
        <Box className={bz.drawerHeadRow}>
          <Box>
            <Text component="span" display="block" className={bz.drawerKicker}>
              {t("drawer.kicker")}
            </Text>
            <Drawer.Title className={bz.drawerTitle}>
              {isNew ? (
                t("drawer.addTitle")
              ) : (
                <>
                  {displayName || renderBusinessName(record ? { profileName: record.name, profileNameEng: record.nameEng } : null)}
                  {meta.refCode ? (
                    <>
                      {" · "}
                      <Text component="span" className={bz.mono} translate="no">
                        {meta.refCode}
                      </Text>
                    </>
                  ) : null}
                </>
              )}
            </Drawer.Title>
          </Box>
          <ActionIcon variant="subtle" color="gray" size={34} className={bz.closeButton} aria-label={tCommon("actions.close")} onClick={requestClose}>
            <XIcon size={16} weight="bold" />
          </ActionIcon>
        </Box>
        <BranchStepIndicator current={step} done={done} lockedReason={lockedReason} onGo={setStep} />
      </Box>

      <Box className={bz.drawerContent} pos="relative">
        <LoadingOverlay visible={saving} />
        {renderStep()}
      </Box>

      <Box className={bz.drawerFoot}>
        <Button variant="default" onClick={requestClose}>
          {detailMode ? tCommon("actions.close") : tCommon("actions.cancel")}
        </Button>
        {readOnly ? null : (
          <Box className={bz.drawerFootGroup}>
            {step === 2 ? (
              <Button variant="default" disabled={busy} onClick={() => setStep(1)}>
                {tCommon("actions.back")}
              </Button>
            ) : null}
            <ReasonTooltip reason={busy ? null : draftReason}>
              {/* On the last step of a confirmed branch, saving is the only way forward, so it is the primary button. */}
              <Button
                variant={confirmed && step === 2 ? "filled" : "default"}
                color={confirmed && step === 2 ? "brand" : undefined}
                disabled={busy} loading={saving} onClick={handleDraftExit}>
                {tCommon("actions.save")}
              </Button>
            </ReasonTooltip>
            {step === 1 ? (
              <ReasonTooltip reason={busy ? null : nextReason}>
                <Button color="brand" disabled={busy} loading={saving} onClick={handleNext}>
                  {t("drawer.saveContinue")}
                </Button>
              </ReasonTooltip>
            ) : confirmed ? null : (
              <ReasonTooltip reason={busy ? null : confirmReason}>
                <Button color="brand" disabled={busy} onClick={handleConfirm}>
                  {tCommon("confirm.confirm")}
                </Button>
              </ReasonTooltip>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
